<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { RouterLink } from 'vue-router';
import BrandQ from './BrandQ.vue';
import AddWorkspaceModal from './AddWorkspaceModal.vue';
import AppIcon from './AppIcon.vue';
import type { IconName } from '@/lib/icons';
import { api } from '@/api/client';
import { workspacePath } from '@/lib/workspace';
import { useToastStore } from '@/stores/toasts';
import { useInboxStore } from '@/stores/inbox';
import { useAuthStore, type User } from '@/stores/auth';
import { useSidebarStore } from '@/stores/sidebar';
import { useThemeStore } from '@/stores/theme';
import { usePaletteStore } from '@/stores/palette';
import { useEmailStore } from '@/stores/email';
import Avatar from '@/components/Avatar.vue';
import NotificationBell from '@/components/NotificationBell.vue';
import WorkspaceSeal from '@/components/WorkspaceSeal.vue';
import { Menu, MenuTrigger, MenuContent, MenuItem, MenuSeparator, Tooltip } from '@hq/chat-ui';
import { useI18n } from '@/lib/i18n';

const { t } = useI18n();

// `label` is an i18n key; the template resolves it with t() so the labels
// (and their tooltips) re-render when the active locale changes.
interface NavItem { to: string; label: string; icon: IconName; badge?: () => number }

const props = defineProps<{ user: User | null }>();
const auth = useAuthStore();
const inbox = useInboxStore();
const sidebar = useSidebarStore();
const theme = useThemeStore();
const palette = usePaletteStore();
const email = useEmailStore();
const toasts = useToastStore();
const isAdmin = computed(() => props.user?.role === 'admin');
// HQ platform operator (admin of an is_platform tenant; today `tic`). Reveals
// the Platform back-office group. Server-side /v1/admin/* enforces the boundary.
const isOperator = computed(() => auth.isOperator);

// Inbound-email triage badge: items needing a human. Loaded once on mount for
// EVERYONE - admins get the full triage count (admin Email page), and a regular
// member gets their review_reply count so the member "Email replies" nav item
// can appear when a reply has been assigned to them.
onMounted(() => {
  void email.refreshBadge();
});

// Platform-aware shortcut hint: the Cmd glyph on macOS, plain "Ctrl K"
// elsewhere (no stray Unicode glyph on Linux/Windows).
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const kbdHint = isMac ? '⌘K' : 'Ctrl K';

const memberLinks: NavItem[] = [
  { to: '/',          label: 'nav.member.home',      icon: 'house' },
  { to: '/inbox',     label: 'nav.member.inbox',     icon: 'tray', badge: () => inbox.unreadCount },
  { to: '/studio',    label: 'nav.member.studio',    icon: 'layout' },
  { to: '/files',     label: 'nav.member.files',     icon: 'folder-open' },
  { to: '/apps',      label: 'nav.member.apps',      icon: 'app-window' },
  { to: '/tables',    label: 'nav.member.tables',    icon: 'table' },
  { to: '/schedules', label: 'nav.member.schedules', icon: 'calendar-dots' },
  { to: '/activity',  label: 'nav.member.activity',  icon: 'pulse' },
];

// The member "Email replies" item appears only when a reply has been assigned
// to this user (review_reply count > 0) - no clutter for the many members who
// never receive one. Admins use the full Admin > Email surface instead.
const visibleMemberLinks = computed<NavItem[]>(() => {
  if (email.reviewReplyCount > 0) {
    return [
      ...memberLinks,
      {
        to: '/email',
        label: 'nav.member.email_replies',
        icon: 'arrow-bend-up-left',
        badge: () => email.reviewReplyCount,
      },
    ];
  }
  return memberLinks;
});

// Skills lives with the admin config surfaces (Agents / Integrations):
// enabling a skill activates it workspace-wide for every agent, and the
// backend already require_admin's the skill mutations.
const adminLinks: NavItem[] = [
  { to: '/connect',                label: 'nav.admin.connect',           icon: 'plugs-connected' },
  { to: '/agents',                 label: 'nav.admin.agents',            icon: 'head-circuit' },
  { to: '/admin/integrations',     label: 'nav.admin.integrations',      icon: 'plugs' },
  { to: '/skills',                 label: 'nav.admin.skills',            icon: 'books' },
  { to: '/admin/email',            label: 'nav.admin.email',             icon: 'mailbox', badge: () => email.badge },
  { to: '/admin/channels',         label: 'nav.admin.channels',          icon: 'hash' },
  { to: '/admin/business-profile', label: 'nav.admin.business_profile',  icon: 'buildings' },
  { to: '/admin/schedules',        label: 'nav.admin.workspace_schedules', icon: 'calendar-dots' },
  { to: '/admin/audit',            label: 'nav.admin.audit',             icon: 'scroll' },
  { to: '/admin/users',            label: 'nav.admin.users',             icon: 'users-three' },
  { to: '/admin/usage',            label: 'nav.admin.usage',             icon: 'gauge' },
  { to: '/admin/settings',         label: 'nav.admin.settings',          icon: 'gear-six' },
];

// ── Account menu ─────────────────────────────────────────────
// The bottom user card triggers a Reka dropdown (@hq/chat-ui Menu) that opens
// upward (side="top") and portals to <body>, so it floats above the nav
// without being clipped. Reka owns outside-click / Escape / focus return - the
// old getBoundingClientRect math + document listeners are gone. Selecting a
// row auto-closes the menu, so handlers just do their work.
const menuOpen = ref(false);

function signOut(): void {
  auth.signOut();
}

// ── Workspace switcher: the seal row ─────────────────────────
// The workspace IS the nav's top-left anchor: a seal (the brand chip carrying
// the tenant monogram, see WorkspaceSeal.vue) + a two-line lockup (workspace
// name in the display voice over a quiet "HQ · role" byline). The menu lists
// the workspaces this person can reach (GET /v1/api/me/workspaces, matched by
// verified email) as a registry of seals; rows switch via switchTo below.
// With a single membership the row wears the plain brand Q - the seal system
// only materializes the day a second workspace exists.
// Same body-teleported menu mechanics as the account menu above.
interface WorkspaceItem { slug: string; name: string; active: boolean; role?: string }
const workspaces = ref<WorkspaceItem[]>([]);
const wsMenuOpen = ref(false);
const activeWorkspace = computed(() => workspaces.value.find((w) => w.active));
const activeWorkspaceName = computed(
  () => activeWorkspace.value?.name ?? props.user?.tenant?.name ?? t('nav.ws.fallback_name'),
);

async function loadWorkspaces(): Promise<void> {
  try {
    const r = await api.get<{ workspaces: WorkspaceItem[] }>('/v1/api/me/workspaces');
    workspaces.value = r.workspaces ?? [];
  } catch {
    /* leave the current label; the selector still shows the active workspace */
  }
}

// Add-workspace: open the in-product create form. This is the AUTHENTICATED
// path - it POSTs /v1/api/me/workspaces (owner = the session identity, no email
// re-entry, no magic-link round-trip) and lands directly in onboarding. The
// public /get-started/slack + /signup funnel is for logged-OUT visitors only.
const showAddWs = ref(false);
function addWorkspace(): void {
  showAddWs.value = true;
}

// Switch the active workspace by navigating into its `/w/<slug>/` URL space.
// The email-keyed session cookie already authorizes every eligible workspace,
// so this is a plain navigation - we still POST /switch to (a) re-mint the
// cookie's DEFAULT workspace (so a bare `/` or a fresh tab reopens this one)
// and (b) learn the target's onboarding state for a precise first paint. The
// full-page load re-bases the router to the new workspace.
const switching = ref<string | null>(null);
async function switchTo(slug: string): Promise<void> {
  if (switching.value) return;
  switching.value = slug;
  try {
    const r = await api.post<{ onboarded?: boolean }>('/v1/api/me/workspaces/switch', { slug });
    window.location.assign(workspacePath(slug, r.onboarded === false ? '/onboarding' : '/'));
  } catch (e) {
    switching.value = null;
    toasts.error((e as { error?: string }).error ?? t('nav.ws.switch_error'));
  }
}

// Option+digit jumps straight to the n:th workspace in the registry (the menu
// rows advertise the binding). Option rather than Cmd: browsers own Cmd+1..9
// for tab switching and won't reliably yield it to the page. Skipped while
// typing so Option-key character entry in inputs stays intact.
const altHint = isMac ? '⌥' : 'Alt ';
function onWsShortcut(e: KeyboardEvent): void {
  if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
  const m = /^Digit([1-9])$/.exec(e.code);
  if (!m) return;
  const el = e.target as HTMLElement | null;
  if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
  const w = workspaces.value[Number(m[1]) - 1];
  if (!w || w.active) return;
  e.preventDefault();
  wsMenuOpen.value = false;
  void switchTo(w.slug);
}

onMounted(() => {
  void loadWorkspaces();
  document.addEventListener('keydown', onWsShortcut);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onWsShortcut);
});
</script>

<template>
  <nav class="nav" :class="{ 'nav--rail': sidebar.collapsed }" :aria-label="t('nav.landmark')">
    <!-- Seal cell: the workspace anchors the top-left in a header-h-tall
         cell with a bottom border (it lines up with the content top-bar
         across every view). The anchor is the seal row - tenant seal +
         name over a quiet "HQ · role" byline - and doubles as the
         workspace-switcher trigger. Single-membership principals see the
         plain brand Q in the row instead of a seal. In rail mode the
         cell keeps just the seal (identity survives the collapse); the
         collapse/expand control lives on the seam handle below, not in
         the cell. -->
    <div class="nav__header">
      <!-- Workspace switcher: the seal row (expanded) or rail seal (collapsed)
           triggers a Reka dropdown that drops down in the expanded nav and
           flies right out of the 48px rail. Reka portals + positions it
           (collision-aware), so the old getBoundingClientRect math is gone. -->
      <Menu v-if="user" v-model:open="wsMenuOpen">
        <!-- The seal is the menu trigger. One MenuTrigger per layout (collapsed
             rail vs expanded row) so each wraps a single button as-child - a
             v-if/v-else INSIDE one MenuTrigger leaves a comment node in the
             as-child slot. (No tooltip here: nesting a tooltip trigger onto a
             dropdown trigger proved fragile in Reka; the workspace name is the
             aria-label + shown in the menu.) -->
        <MenuTrigger v-if="sidebar.collapsed">
          <button
            class="nav__seal-rail"
            type="button"
            :aria-label="t('nav.ws.switch')"
          >
            <BrandQ class="nav__brand-q" />
          </button>
        </MenuTrigger>
        <MenuTrigger v-else>
          <button
            class="nav__seal-row"
            :class="{ 'nav__seal-row--open': wsMenuOpen }"
            type="button"
            :aria-label="t('nav.ws.switch')"
          >
            <BrandQ class="nav__brand-q" />
            <span class="nav__seal-name">{{ activeWorkspaceName }}</span>
            <AppIcon name="caret-down" class="nav__seal-caret" :size="12" />
          </button>
        </MenuTrigger>
        <MenuContent
          :side="sidebar.collapsed ? 'right' : 'bottom'"
          align="start"
          :label="t('nav.ws.switch')"
          min-width="240px"
        >
          <div class="account-menu__head">
            <span class="account-menu__name">{{ t('nav.ws.heading') }}</span>
            <span v-if="user.email" class="account-menu__email">{{ user.email }}</span>
          </div>
          <MenuSeparator />
          <template v-for="(w, i) in workspaces" :key="w.slug">
            <!-- Active workspace: display-only, checked. -->
            <div
              v-if="w.active"
              class="nav__ws-item nav__ws-item--active"
              aria-current="true"
            >
              <WorkspaceSeal :name="w.name" :slug="w.slug" minted :size="22" />
              <span class="nav__ws-row-meta">
                <span class="nav__ws-row-name">{{ w.name }}</span>
                <span v-if="w.role" class="nav__ws-row-role">{{ w.role }}</span>
              </span>
              <AppIcon name="check" class="nav__ws-row-check" :size="14" />
            </div>
            <!-- Reachable workspace: select to switch (re-mint the session). -->
            <MenuItem
              v-else
              :disabled="switching !== null"
              @select="switchTo(w.slug)"
            >
              <span v-if="switching === w.slug" class="nav__ws-spinbox">
                <AppIcon name="circle-notch" class="nav__ws-spin" :size="14" />
              </span>
              <WorkspaceSeal v-else :name="w.name" :slug="w.slug" :size="22" />
              <span class="nav__ws-row-meta">
                <span class="nav__ws-row-name">{{ w.name }}</span>
                <span v-if="w.role" class="nav__ws-row-role">{{ w.role }}</span>
              </span>
              <kbd v-if="i < 9" class="nav__ws-kbd">{{ altHint }}{{ i + 1 }}</kbd>
            </MenuItem>
          </template>
          <MenuSeparator />
          <MenuItem @select="addWorkspace">
            <AppIcon name="plus" :size="16" />
            <span>{{ t('nav.ws.add') }}</span>
          </MenuItem>
        </MenuContent>
      </Menu>
      <BrandQ v-else class="nav__brand-q" />
    </div>

    <div class="nav__body">
      <!-- Command palette (Cmd/Ctrl-K): a search box pinned above the nav
           links. Opens the palette - search conversations + run commands;
           Quick ask is its first command. -->
      <Tooltip :label="sidebar.collapsed ? t('nav.search.tooltip', { kbd: kbdHint }) : undefined" side="right">
        <button
          type="button"
          class="nav__quickask"
          @click="palette.openPanel()"
        >
          <AppIcon name="magnifying-glass" class="nav__quickask-icon" :size="18" />
          <span class="nav__quickask-label">{{ t('nav.search.label') }}</span>
          <kbd class="nav__quickask-kbd">{{ kbdHint }}</kbd>
        </button>
      </Tooltip>

      <ul class="nav__group" role="list">
        <li v-for="link in visibleMemberLinks" :key="link.to">
          <Tooltip :label="sidebar.collapsed ? t(link.label) : undefined" side="right">
            <RouterLink
              :to="link.to"
              class="nav__link"
              active-class="nav__link--active"
            >
              <AppIcon :name="link.icon" class="nav__link-icon" :size="18" />
              <span class="nav__link-label">{{ t(link.label) }}</span>
              <span
                v-if="link.badge && link.badge() > 0"
                class="nav__badge"
                :aria-label="t('nav.badge.unread')"
              >{{ link.badge() }}</span>
            </RouterLink>
          </Tooltip>
        </li>
      </ul>

      <template v-if="isAdmin">
        <div class="nav__divider">{{ t('nav.divider.admin') }}</div>
        <ul class="nav__group" role="list">
          <li v-for="link in adminLinks" :key="link.to">
            <Tooltip :label="sidebar.collapsed ? t(link.label) : undefined" side="right">
              <RouterLink
                :to="link.to"
                class="nav__link"
                active-class="nav__link--active"
              >
                <AppIcon :name="link.icon" class="nav__link-icon" :size="18" />
                <span class="nav__link-label">{{ t(link.label) }}</span>
                <span
                  v-if="link.badge && link.badge() > 0"
                  class="nav__badge"
                  :aria-label="t('nav.badge.needs_attention')"
                >{{ link.badge() }}</span>
              </RouterLink>
            </Tooltip>
          </li>
        </ul>
      </template>

      <!-- Platform back-office: visible only to HQ operators (tic). One entry
           into the tabbed /operator console (signups / invites / tenants). -->
      <template v-if="isOperator">
        <div class="nav__divider">{{ t('nav.divider.platform') }}</div>
        <ul class="nav__group" role="list">
          <li>
            <Tooltip :label="sidebar.collapsed ? t('nav.operator') : undefined" side="right">
              <RouterLink
                to="/operator"
                class="nav__link"
                active-class="nav__link--active"
              >
                <AppIcon name="shield-check" class="nav__link-icon" :size="18" />
                <span class="nav__link-label">{{ t('nav.operator') }}</span>
              </RouterLink>
            </Tooltip>
          </li>
        </ul>
      </template>

      <hr class="nav__rule" />

      <ul class="nav__group" role="list">
        <!-- Global bell: a nav row in the utility group, so the unread
             badge is on every surface without crowding the page chrome.
             The panel pops up from here (the trigger sits in the bottom
             half, so NotificationBell anchors it upward). -->
        <li>
          <NotificationBell variant="nav" :collapsed="sidebar.collapsed" />
        </li>
        <li>
          <Tooltip :label="sidebar.collapsed ? t('nav.help') : undefined" side="right">
            <RouterLink
              to="/help"
              class="nav__link"
              active-class="nav__link--active"
            >
              <AppIcon name="question" class="nav__link-icon" :size="18" />
              <span class="nav__link-label">{{ t('nav.help') }}</span>
            </RouterLink>
          </Tooltip>
        </li>
      </ul>

    </div>

    <!-- Collapse/expand: its own non-scrolling row pinned between the
         body and the footer hairline (the body scrolls, so the button
         can't live inside it or it falls below the fold on short
         viewports). Right-aligned; centered in rail, where the caret
         flips to expand. Present even when a route forces the rail
         (Studio) - the toggle releases the force so the user isn't stuck. -->
    <div class="nav__collapse-row">
      <Tooltip :label="sidebar.collapsed ? t('nav.collapse.expand') : t('nav.collapse.collapse')">
        <button
          class="icon-btn nav__collapse"
          type="button"
          :aria-label="sidebar.collapsed ? t('nav.collapse.expand') : t('nav.collapse.collapse')"
          @click="sidebar.toggle()"
        >
          <AppIcon :name="sidebar.collapsed ? 'caret-double-right' : 'caret-double-left'" :size="18" />
        </button>
      </Tooltip>
    </div>

    <!-- Footer: just the account card (bottom-left). The card is the
         account-menu trigger (Profile / theme / sign-out live in the
         teleported menu below). Logo + collapse own the top, account
         sits at the bottom - the Linear / Notion / Vercel convention;
         notifications live as a row in the utility group above. In rail
         everything centers. -->

    <div class="nav__footer">
      <!-- Account menu: the user card triggers a Reka dropdown that opens
           upward (side="top") and portals to <body>. Reka owns outside-click /
           Escape / focus return. Theme toggle keeps the menu open (it calls
           preventDefault on select); the rest close on select. -->
      <Menu v-if="user" v-model:open="menuOpen">
        <!-- The card IS the menu trigger. (No tooltip wrapper: a disabled
             TooltipRoot around the trigger swallowed the click, and nesting a
             tooltip trigger onto a dropdown trigger proved fragile in Reka. The
             name is the menu head + visible on the card when expanded.) -->
        <MenuTrigger>
          <button
            class="nav__user-card"
            :class="{ 'nav__user-card--open': menuOpen }"
            type="button"
          >
            <!-- Compact single row: small avatar + name. The email lives in
                 the account menu's head, so the card doesn't repeat it. -->
            <Avatar :name="user.display_name" :src="user.avatar_url" size="sm" solid />
            <div class="nav__user-meta">
              <span class="nav__user-name">{{ user.display_name }}</span>
            </div>
            <AppIcon name="dots-three" class="nav__user-more" :size="16" />
          </button>
        </MenuTrigger>
        <MenuContent side="top" align="start" :label="t('nav.account.label')" min-width="220px">
          <div class="account-menu__head">
            <span class="account-menu__name">{{ user.display_name }}</span>
            <span class="account-menu__email">{{ user.email }}</span>
          </div>
          <MenuSeparator />
          <MenuItem as-child>
            <RouterLink :to="{ name: 'me' }">
              <AppIcon name="user-circle" :size="16" />
              <span>{{ t('nav.account.profile') }}</span>
            </RouterLink>
          </MenuItem>
          <MenuItem as-child>
            <RouterLink :to="{ name: 'developer-apps' }">
              <AppIcon name="code" :size="16" />
              <span>{{ t('nav.account.developer_apps') }}</span>
            </RouterLink>
          </MenuItem>
          <MenuItem @select="(e) => { e.preventDefault(); theme.toggle(); }">
            <AppIcon :name="theme.effective === 'dark' ? 'sun' : 'moon'" :size="16" />
            <span>{{ theme.effective === 'dark' ? t('nav.account.light_theme') : t('nav.account.dark_theme') }}</span>
          </MenuItem>
          <MenuSeparator />
          <MenuItem danger @select="signOut">
            <AppIcon name="sign-out" :size="16" />
            <span>{{ t('nav.account.sign_out') }}</span>
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>

    <!-- Authenticated create-workspace form (teleports to body itself). -->
    <AddWorkspaceModal :open="showAddWs" @close="showAddWs = false" />
  </nav>
</template>

<style scoped lang="scss">
@layer layout {
  .nav {
    background: var(--surface-nav);
    border-right: 1px solid var(--nav-border);
    padding: 0;
    display: flex;
    flex-direction: column;
    // Bound to the viewport (not min-height) so the nav never grows past the
    // screen: header + footer pin (flex-shrink:0) and ONLY .nav__body scrolls
    // (flex:1 + overflow-y:auto) on short windows, instead of the whole column
    // scrolling/clipping the account card out of reach.
    height: 100dvh;
    color: var(--nav-ink);

    // Logo cell - header-h tall with a bottom border. The border-bottom
    // meets the nav's border-right at a square corner and lines up with
    // the content top-bar. The seal row owns the full cell width.
    &__header {
      flex-shrink: 0;
      height: var(--header-h);
      display: flex;
      align-items: center;
      padding: 0 var(--space-4);
      border-bottom: 1px solid var(--nav-border);
    }

    // Collapse/expand - a pinned row of its own between the scrolling
    // body and the footer hairline; just the icon button, right-aligned.
    &__collapse-row {
      flex-shrink: 0;
      display: flex;
      justify-content: flex-end;
      padding: 0 var(--space-2) var(--space-1);
    }

    // Footer - just the account card. A hairline separates it from the
    // link list above.
    &__footer {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
      padding: var(--space-2);
      border-top: 1px solid var(--nav-rule);
    }
    &__brand-q {
      /* the brand Q is now an inline outlined SVG (BrandQ.vue); just size it */
      width: 28px;
      height: 28px;
    }
    // Seal row - the workspace anchor doubling as the switcher trigger,
    // filling the logo cell up to the collapse toggle. The negative inline
    // margin lets the hover pill bleed into the cell padding while the
    // seal stays optically where the Q sat.
    &__seal-row {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: 4px var(--space-2);
      margin-left: calc(-1 * var(--space-2));
      border: 0;
      border-radius: var(--radius-button);
      background: transparent;
      color: var(--nav-ink);
      font: inherit;
      text-align: left;
      cursor: pointer;
      transition: background var(--dur-1) var(--ease-out);

      &:hover,
      &--open { background: var(--nav-item-hover); }
      &:focus-visible { outline: 2px solid var(--border-focus); outline-offset: 2px; }
    }
    // Workspace name in the display voice - the same register as page
    // titles, scaled to the nav.
    &__seal-name {
      flex: 1;
      min-width: 0;
      font-family: var(--font-display);
      font-size: var(--text-sm);
      font-weight: 600;
      color: var(--nav-ink-strong);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    &__seal-caret {
      flex-shrink: 0;
      color: var(--nav-ink);
      opacity: 0.6;
      transition: transform var(--dur-1) var(--ease-out);
    }
    &__seal-row--open .nav__seal-caret { transform: rotate(180deg); }

    // Rail seal - tenant identity in the 48px column (the header cell is
    // the expand caret there); same icon-square footprint as the links.
    &__seal-rail {
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      margin-inline: auto;
      padding: 0;
      border: 0;
      border-radius: var(--radius-button);
      background: transparent;
      cursor: pointer;
      transition: background var(--dur-1) var(--ease-out);

      &:hover { background: var(--nav-item-hover); }
      &:focus-visible { outline: 2px solid var(--border-focus); outline-offset: 2px; }
    }

    // Vertical rhythm runs tight (space-3 gaps, space-2 padding) so the
    // full admin nav fits a 14-inch laptop viewport without scrolling.
    &__body {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--space-3);
      min-height: 0;
      overflow-y: auto;
      padding: var(--space-2);
    }

    // Quick-ask trigger - reads as a subtle command box (hairline border,
    // muted ink, a kbd hint pinned right). Lives at the top of the body.
    &__quickask {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      width: 100%;
      padding: 6px var(--space-3);
      border: 1px solid var(--nav-rule);
      border-radius: var(--radius-button);
      background: transparent;
      color: var(--nav-ink);
      font: inherit;
      font-size: var(--text-sm);
      font-weight: 500;
      cursor: pointer;
      transition: background var(--dur-1) var(--ease-out),
                  border-color var(--dur-1) var(--ease-out),
                  color var(--dur-1) var(--ease-out);

      &:hover {
        background: var(--nav-item-hover);
        border-color: var(--nav-border);
        color: var(--nav-ink-strong);
      }
      &:focus-visible {
        outline: 2px solid var(--border-focus);
        outline-offset: 2px;
      }
    }
    &__quickask-icon { flex-shrink: 0; opacity: 0.85; }
    &__quickask-label { flex: 1; text-align: left; }
    &__quickask-kbd {
      flex-shrink: 0;
      font-family: var(--font-mono);
      font-size: 10px;
      line-height: 1.4;
      color: var(--nav-ink-subtle);
      border: 1px solid var(--nav-rule);
      border-radius: var(--radius-chip);
      padding: 1px 5px;
    }

    &__group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    // Quiet uppercase section label for the "Admin" divider - no
    // glyph, sans, subtle nav ink.
    &__divider {
      font-family: var(--font-sans);
      font-size: var(--text-xs);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: var(--tracking-wide);
      color: var(--nav-ink-subtle);
      padding: var(--space-3) var(--space-3) var(--space-1);
    }
    &__rule {
      border: 0;
      border-top: 1px solid var(--nav-rule);
      margin: var(--space-2) var(--space-3);
    }

    // Nav link - muted ink at rest; hover and active lift to a soft
    // rounded PILL (the Lovable/Replit selected look). No yellow
    // left-edge bar; the brand spark rides on the active icon only.
    &__link {
      position: relative;
      display: flex;
      align-items: center;
      gap: var(--space-3);
      // 6px block padding (a notch under space-2): 17 admin-visible rows
      // at 8px each overflow a 14-inch laptop viewport.
      padding: 6px var(--space-3);
      border-radius: var(--radius-button);
      color: var(--nav-ink);
      text-decoration: none;
      font-size: var(--text-sm);
      font-weight: 500;
      transition:
        background var(--dur-1) var(--ease-out),
        color var(--dur-1) var(--ease-out);

      &:hover {
        background: var(--nav-item-hover);
        color: var(--nav-ink-strong);
      }

      &--active {
        background: var(--nav-item-active);
        color: var(--nav-ink-strong);
        font-weight: 600;
      }
    }
    &__link-icon {
      flex-shrink: 0;
      opacity: 0.85;
      transition: opacity var(--dur-1) var(--ease-out),
                  color var(--dur-1) var(--ease-out);
    }
    &__link:hover .nav__link-icon { opacity: 1; }
    // Active icon is monochrome - it inherits the strong nav ink from
    // the link. The pill fill + full-strength ink/weight carry "active";
    // yellow doesn't read on a small light-pill icon (it's reserved for
    // the brand Q, focus rings, and the one key CTA per view).
    &__link--active .nav__link-icon { opacity: 1; }
    &__link-label { flex: 1; }
    &__badge {
      font-family: var(--font-mono);
      font-size: 10px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: var(--radius-chip);
      background: var(--accent-default);
      color: var(--accent-text);
      min-width: 1.5em;
      text-align: center;
      letter-spacing: var(--tracking-wide);
    }

    // Account card - a compact single-line row (small avatar + name);
    // the email lives in the account menu's head.
    &__user-card {
      width: 100%;
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-1) var(--space-2);
      border: 0;
      border-radius: var(--radius-input);
      background: transparent;
      color: inherit;
      font: inherit;
      text-align: left;
      cursor: pointer;
      transition: background var(--dur-1) var(--ease-out);

      &:hover,
      &--open { background: var(--nav-item-hover); }
      &:hover .nav__user-more,
      &--open .nav__user-more { color: var(--nav-ink-strong); }
      &:focus-visible {
        outline: 2px solid var(--border-focus);
        outline-offset: 2px;
      }
    }
    &__user-meta {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      line-height: 1.2;
    }
    &__user-name {
      font-size: var(--text-sm);
      font-weight: 600;
      color: var(--nav-ink-strong);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    &__user-more {
      flex-shrink: 0;
      color: var(--nav-ink-subtle);
      transition: color var(--dur-1) var(--ease-out);
    }
  }

  // ────────── Rail mode (collapsed) ──────────
  // 48px-wide column showing icons only. Toggling adds .nav--rail to
  // the root; everything text-bearing collapses, everything icon-
  // bearing centers. Native `:title` tooltips surface the labels.
  .nav--rail {
    align-items: stretch;

    // Logo cell: center the Q; keep the header-h height + bottom border
    // so the rail's top corner still squares off against the content.
    .nav__header { justify-content: center; padding: 0; }

    // Body + footer center their contents in the 48px column.
    .nav__body   { padding: var(--space-3) 0; }
    .nav__footer { align-items: center; padding: var(--space-2) 0; }

    // Collapse row: centered in the 48px column (it right-aligns when
    // expanded).
    .nav__collapse-row { justify-content: center; padding-inline: 0; }

    // User card: avatar only, centered. Drop the card chrome so the
    // avatar floats cleanly in the rail.
    .nav__user-card {
      padding: 0;
      justify-content: center;
      &:hover { background: transparent; }
    }
    .nav__user-meta,
    .nav__user-more { display: none; }

    // Links: collapse the label + badge; the icon is the link. Constrain
    // the link box to a centered icon-sized square so the hover / active
    // fill is a tidy rounded square - not a full-rail-width band (which
    // looked weird against the 48px column).
    .nav__link {
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-inline: auto;
      padding: 0;
      gap: 0;
    }
    .nav__link-label,
    .nav__badge { display: none; }
    .nav__link--active {
      background: var(--nav-item-active);
    }

    // Quick-ask: collapse to a centered icon square like the links.
    .nav__quickask {
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-inline: auto;
      padding: 0;
      gap: 0;
    }
    .nav__quickask-label,
    .nav__quickask-kbd { display: none; }

    // Divider: in the rail there's no room for the label; collapse
    // it to a thin hairline that still reads as a section break.
    .nav__divider {
      height: 1px;
      padding: 0;
      margin: var(--space-3) var(--space-3);
      overflow: hidden;
      color: transparent;
      background: var(--nav-rule);
    }
    .nav__rule { margin-inline: var(--space-3); }
  }

  // ────────── Drawer mode (phones, < --bp-sm) ──────────
  // On phones AppNav renders inside NavDrawer (a Reka Dialog sheet); it's
  // always expanded with full labels. The collapse/expand toggle is a desktop
  // rail affordance with no meaning here - the drawer dismisses via the
  // hamburger, Escape, a link, or an outside tap - so hide it.
  @media (max-width: 479.98px) {
    .nav__collapse-row { display: none; }
    // The sheet (.navdrawer) is a full-height fixed panel that, with
    // viewport-fit=cover, spans the safe areas; its --surface-nav background
    // fills the notch / home-indicator strips. AppNav fills the sheet and pads
    // its OWN content into the safe area so nothing hides behind the notch,
    // home indicator, or a landscape rounded corner.
    .nav {
      height: 100%;   // fill the sheet (NavDrawer's .navdrawer is height:100dvh)
      padding-top: env(safe-area-inset-top, 0px);
      padding-bottom: env(safe-area-inset-bottom, 0px);
      padding-left: env(safe-area-inset-left, 0px);
    }
  }
}
</style>

<!-- Unscoped: the menus render inside the shared @hq/chat-ui Menu's <body>
     portal, so scoped class hashes wouldn't reach them. The panel + row chrome
     come from the shared Menu (.hq-menu / .hq-menu__item); what's left here is
     the menu HEAD and the workspace-row extras. Selectors stay prefixed. -->
<style lang="scss">
.account-menu__head {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: var(--space-2) var(--space-3) var(--space-3);
}
.account-menu__name {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--ink-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-menu__email {
  font-size: var(--text-xs);
  color: var(--ink-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Workspace registry rows (the switcher menu). Switchable rows are <MenuItem>
   and inherit the shared .hq-menu__item layout; the ACTIVE row is a display-
   only div, so it carries the same row layout here (it's not a MenuItem). */
.nav__ws-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-input);
  font-size: var(--text-sm);
  color: var(--ink-primary);
  cursor: default;
}
.nav__ws-item--active { font-weight: 600; }
.nav__ws-row-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.2;
}
.nav__ws-row-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nav__ws-row-role {
  font-size: var(--text-xs);
  color: var(--ink-muted);
}
.nav__ws-row-check {
  flex-shrink: 0;
  color: var(--ink-primary);
}
/* The Option+digit binding, advertised the way Quick ask advertises ⌘K. */
.nav__ws-kbd {
  flex-shrink: 0;
  font-family: var(--font-mono);
  font-size: 10px;
  line-height: 1.4;
  color: var(--ink-muted);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-chip);
  padding: 1px 5px;
}
/* Spinner keeps the seal's 22px footprint so the row doesn't shift. */
.nav__ws-spinbox {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
}
.nav__ws-spin { animation: nav-ws-spin 900ms linear infinite; }
@keyframes nav-ws-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
